import React from 'react'

import { SOCIAL_BRANDS, SocialBrand, normalizeSocialType } from '@/constants/socialBrands'
import { VCardSocialLinkTypes } from '@/lib/types/VCardT'
import { cn } from '@/lib/utils'

const BRANDS_BY_TYPE = Object.fromEntries(
  SOCIAL_BRANDS.map((brand) => [brand.type, brand])
) as Record<VCardSocialLinkTypes, SocialBrand | undefined>

/** Resolves a stored type — legacy names included — to its brand row. */
export const getSocialBrand = (type: VCardSocialLinkTypes | string) =>
  BRANDS_BY_TYPE[normalizeSocialType(type)]

/**
 * The brand's own colours as CSS variables, so a single class list can carry both themes.
 * Near-black marks (X, Threads, GitHub…) flip to a white tile with a dark glyph on a dark
 * surface — otherwise they'd disappear into it.
 */
export const brandVars = (brand: SocialBrand): React.CSSProperties =>
  ({
    '--sb': brand.color,
    '--sb-on': brand.onColor ?? '#FFFFFF',
    '--sb-dark': brand.colorDark ?? brand.color,
    '--sb-on-dark': brand.onColorDark ?? brand.onColor ?? '#FFFFFF',
  }) as React.CSSProperties

type GlyphProps = {
  type: VCardSocialLinkTypes | string
  size?: number
  className?: string
}

/** Bare brand mark, inheriting `currentColor`. */
export const SocialGlyph = ({ type, size = 16, className }: GlyphProps) => {
  const brand = getSocialBrand(type)
  if (!brand) return null

  return (
    <svg
      aria-hidden
      viewBox="0 0 24 24"
      width={size}
      height={size}
      className={cn('shrink-0', className)}
    >
      <path fill="currentColor" d={brand.path} />
    </svg>
  )
}

type TileProps = {
  type: VCardSocialLinkTypes | string
  /** Tile edge in px; the glyph is sized to ~56% of it. */
  size?: number
  radius?: number
  /** `solid` fills with the brand colour, `tint` keeps a 12% wash behind a brand-coloured mark. */
  variant?: 'solid' | 'tint'
  className?: string
}

/** Brand mark on its own colour — the one treatment used everywhere a network is named. */
export const SocialTile = ({
  type,
  size = 28,
  radius = 9,
  variant = 'solid',
  className,
}: TileProps) => {
  const brand = getSocialBrand(type)
  if (!brand) return null

  const solid = variant === 'solid'

  return (
    <span
      aria-hidden
      style={{ ...brandVars(brand), width: size, height: size, borderRadius: radius }}
      className={cn(
        'inline-flex shrink-0 items-center justify-center',
        solid
          ? 'bg-[var(--sb)] text-[var(--sb-on)] dark:bg-[var(--sb-dark)] dark:text-[var(--sb-on-dark)]'
          : 'bg-[color-mix(in_srgb,var(--sb)_14%,transparent)] text-[var(--sb)] dark:bg-[color-mix(in_srgb,var(--sb-dark)_18%,transparent)] dark:text-[var(--sb-dark)]',
        className
      )}
    >
      <SocialGlyph type={type} size={Math.round(size * 0.56)} />
    </span>
  )
}

export default SocialTile
